import { AuthErrorCodes } from "firebase/auth";
import { isObject } from "wombat-global/src/utilities";

export type LoginAuthErrorField = "email" | "password";

const SESSION_AUTH_ERROR_CODES: string[] = [
  AuthErrorCodes.TOKEN_EXPIRED,
  AuthErrorCodes.USER_DISABLED,
  AuthErrorCodes.USER_DELETED,
  // Firebase normalizes INVALID_REFRESH_TOKEN without an exported constant.
  "auth/invalid-refresh-token",
];

const EMAIL_AUTH_ERROR_CODES: string[] = [
  AuthErrorCodes.USER_DELETED,
  AuthErrorCodes.USER_DISABLED,
];

const PASSWORD_AUTH_ERROR_CODES: string[] = [
  AuthErrorCodes.INVALID_PASSWORD,
  AuthErrorCodes.INVALID_LOGIN_CREDENTIALS,
];

const EXPECTED_LOGIN_AUTH_ERROR_CODES: string[] = [
  ...PASSWORD_AUTH_ERROR_CODES,
  ...EMAIL_AUTH_ERROR_CODES,
];

function getStringField(value: unknown, field: string): string | undefined {
  if (!isObject(value)) {
    return undefined;
  }

  const fieldValue = value[field];
  if (typeof fieldValue === "string") {
    return fieldValue;
  }

  return undefined;
}

export function getAuthErrorMessage(error: unknown): string | undefined {
  return getStringField(error, "message");
}

export function getAuthErrorCode(error: unknown): string | undefined {
  return getStringField(error, "code");
}

export function isExpectedLoginAuthErrorCode(
  code: string | undefined,
): boolean {
  if (!code) {
    return false;
  }

  return EXPECTED_LOGIN_AUTH_ERROR_CODES.includes(code);
}

export function isSessionAuthError(error: unknown): boolean {
  const code = getAuthErrorCode(error);
  if (!code) {
    return false;
  }
  return SESSION_AUTH_ERROR_CODES.includes(code);
}

export function isEmailAuthError(error: unknown): boolean {
  const code = getAuthErrorCode(error);
  if (!code) {
    return false;
  }
  return EMAIL_AUTH_ERROR_CODES.includes(code);
}

export function isPasswordAuthError(error: unknown): boolean {
  const code = getAuthErrorCode(error);
  if (!code) {
    return false;
  }
  return PASSWORD_AUTH_ERROR_CODES.includes(code);
}

export function hasAuthErrorCode(error: unknown, code: string): boolean {
  return getAuthErrorCode(error) === code;
}

export function getAuthErrorAndMessage(
  error: unknown,
): { code: string; message: string | undefined } | undefined {
  const code = getStringField(error, "code");
  const message = getStringField(error, "message");

  if (code === AuthErrorCodes.USER_DISABLED) {
    return { code, message: "User is inactive / disabled" };
  }

  if (code) {
    return { code, message };
  }

  return;
}

export function isExpectedLoginAuthError(error: unknown): boolean {
  const code = getAuthErrorCode(error);
  if (isExpectedLoginAuthErrorCode(code)) {
    return true;
  }

  const message = getStringField(error, "message");
  if (!message) {
    return false;
  }

  return EXPECTED_LOGIN_AUTH_ERROR_CODES.some((authErrorCode) =>
    message.includes(authErrorCode),
  );
}
